import React, { useState } from 'react';
import {
  DndContext,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { useDroppable } from '@dnd-kit/core';
import { DraggableMachine } from './DraggableMachine';
import { MaintenanceEmployee, Machine } from '../../types/maintenance';

interface ScheduleBuilderProps {
  employees: MaintenanceEmployee[];
  availableMachines: Machine[];
  assignments: Record<string, string[]>; // employeeId -> machineIds[]
  onAssignmentChange: (assignments: Record<string, string[]>) => void;
}

interface EmployeeDropZoneProps {
  employee: MaintenanceEmployee;
  assignedMachines: Machine[];
  onRemoveMachine: (machineId: string) => void;
}

const EmployeeDropZone: React.FC<EmployeeDropZoneProps> = ({
  employee,
  assignedMachines,
  onRemoveMachine,
}) => {
  const { setNodeRef, isOver } = useDroppable({
    id: `employee-${employee.id}`,
    data: {
      employeeId: employee.id,
    },
  });

  const hasNoAssignments = assignedMachines.length === 0;

  return (
    <div
      ref={setNodeRef}
      className={`
        bg-white rounded-lg border-2 p-4 min-h-[200px] transition-all
        ${isOver ? 'border-orange-500 bg-orange-50' : 'border-gray-200'}
        ${hasNoAssignments ? 'bg-yellow-50 border-yellow-300' : ''}
      `}
    >
      {/* Employee Header */}
      <div className="mb-4 pb-3 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <div className="flex-1 min-w-0">
            <h3 className="font-semibold text-gray-900 truncate">
              {employee.name}
            </h3>
            <p className="text-sm text-gray-500 truncate">{employee.email}</p>
            <p className="text-xs font-mono text-orange-600 mt-1">
              {employee.employeeCode}
            </p>
          </div>
          <div className="flex-shrink-0 ml-3">
            <span
              className={`
                inline-flex items-center px-3 py-1 rounded-full text-sm font-semibold
                ${
                  hasNoAssignments
                    ? 'bg-yellow-100 text-yellow-800'
                    : 'bg-blue-100 text-blue-800'
                }
              `}
            >
              {assignedMachines.length} máquina{assignedMachines.length !== 1 ? 's' : ''}
            </span>
          </div>
        </div>
      </div>

      {/* Assigned Machines */}
      <div className="space-y-2">
        {assignedMachines.length > 0 ? (
          assignedMachines.map((machine) => (
            <div
              key={machine.id}
              className="bg-gray-50 border border-gray-200 rounded-lg p-3 flex items-center justify-between group hover:bg-gray-100 transition-colors"
            >
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <svg
                    className="w-4 h-4 flex-shrink-0 text-gray-500"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 19h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v10a2 2 0 002 2zM9 9h6v6H9V9z"
                    />
                  </svg>
                  <span className="font-mono font-bold text-sm">
                    {machine.code}
                  </span>
                </div>
                <div className="flex items-center gap-1 text-xs text-gray-600">
                  <svg
                    className="w-3 h-3 flex-shrink-0"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                    />
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                    />
                  </svg>
                  <span className="truncate">{machine.location}</span>
                </div>
              </div>
              <button
                onClick={() => onRemoveMachine(machine.id)}
                className="flex-shrink-0 ml-2 p-1.5 text-red-600 hover:bg-red-100 rounded-md transition-colors opacity-0 group-hover:opacity-100"
                title="Remover máquina"
              >
                <svg
                  className="w-4 h-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>
          ))
        ) : (
          <div className="text-center py-8 text-gray-400">
            <svg
              className="w-12 h-12 mx-auto mb-2"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 6v6m0 0v6m0-6h6m-6 0H6"
              />
            </svg>
            <p className="text-sm">
              Arraste máquinas aqui
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export const ScheduleBuilder: React.FC<ScheduleBuilderProps> = ({
  employees,
  availableMachines,
  assignments,
  onAssignmentChange,
}) => {
  const [activeMachine, setActiveMachine] = useState<Machine | null>(null);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    })
  );

  const handleDragStart = (event: DragStartEvent) => {
    const machine = event.active.data.current?.machine as Machine;
    if (machine) {
      setActiveMachine(machine);
    }
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    setActiveMachine(null);

    if (!over) return;

    const machine = active.data.current?.machine as Machine;
    const targetEmployeeId = over.data.current?.employeeId as string;

    if (!machine || !targetEmployeeId) return;

    // Remove machine from all employees
    const newAssignments = { ...assignments };
    Object.keys(newAssignments).forEach((employeeId) => {
      newAssignments[employeeId] = newAssignments[employeeId].filter(
        (id) => id !== machine.id
      );
    });

    // Add machine to target employee
    if (!newAssignments[targetEmployeeId]) {
      newAssignments[targetEmployeeId] = [];
    }
    if (!newAssignments[targetEmployeeId].includes(machine.id)) {
      newAssignments[targetEmployeeId].push(machine.id);
    }

    onAssignmentChange(newAssignments);
  };

  const handleRemoveMachine = (employeeId: string, machineId: string) => {
    const newAssignments = { ...assignments };
    newAssignments[employeeId] = newAssignments[employeeId].filter(
      (id) => id !== machineId
    );
    onAssignmentChange(newAssignments);
  };

  const getAssignedMachines = (employeeId: string): Machine[] => {
    if (!Array.isArray(availableMachines)) {
      return [];
    }
    const machineIds = assignments[employeeId] || [];
    return machineIds
      .map((id) => availableMachines.find((m) => m.id === id))
      .filter((m): m is Machine => m !== undefined);
  };

  const getUnassignedMachines = (): Machine[] => {
    if (!Array.isArray(availableMachines)) {
      return [];
    }
    const assignedMachineIds = new Set(
      Object.values(assignments).flat()
    );
    return availableMachines.filter((m) => !assignedMachineIds.has(m.id));
  };

  const unassignedMachines = getUnassignedMachines();
  const activeEmployees = employees.filter((e) => e.status === 'active');

  return (
    <DndContext
      sensors={sensors}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
    >
      <div className="space-y-6">
        {/* Available Machines Section */}
        <div className="bg-white rounded-lg shadow p-4">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            Máquinas Disponíveis ({unassignedMachines.length})
          </h3>
          {unassignedMachines.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
              {unassignedMachines.map((machine) => (
                <DraggableMachine
                  key={machine.id}
                  machine={machine}
                  isDragging={activeMachine?.id === machine.id}
                />
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-gray-400">
              <svg
                className="w-12 h-12 mx-auto mb-2"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              <p className="text-sm">Todas as máquinas foram atribuídas</p>
            </div>
          )}
        </div>

        {/* Employee Assignments Section */}
        <div>
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            Atribuições por Funcionário
          </h3>
          {activeEmployees.length > 0 ? (
            <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4">
              {activeEmployees.map((employee) => (
                <EmployeeDropZone
                  key={employee.id}
                  employee={employee}
                  assignedMachines={getAssignedMachines(employee.id)}
                  onRemoveMachine={(machineId) =>
                    handleRemoveMachine(employee.id, machineId)
                  }
                />
              ))}
            </div>
          ) : (
            <div className="text-center py-12 bg-white rounded-lg shadow">
              <svg
                className="w-16 h-16 mx-auto text-gray-400 mb-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
                />
              </svg>
              <p className="text-gray-500">
                Nenhum funcionário ativo cadastrado.
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Drag Overlay */}
      <DragOverlay>
        {activeMachine ? (
          <div className="opacity-80">
            <DraggableMachine machine={activeMachine} isDragging={true} />
          </div>
        ) : null}
      </DragOverlay>
    </DndContext>
  );
};
